import { getField } from "vuex-map-fields";
import { AutomationCreateStepEnum, AutomationTypeEnum } from "../enums";

const me = (state) => state.me;
const automationActions = (state) => state.automationActions;
const automations = (state) => state.automations;

const automationables = (state) => [
  ...state.activeAutomationables,
  ...state.inactiveAutomationables,
];
const globalAutomations = (state) => state.globalAutomations;
const globalSearches = (state) => state.globalSearches;
const searches = (state) => state.searches;
const searchesGroupedByType = (state) => state.searches.reduce((acc, search) => {
  if (!acc[search.model]) {
    // eslint-disable-next-line no-param-reassign
    acc[search.model] = [];
  }

  acc[search.model].push(search);

  return acc;
}, {});

const sortByPendingConfirmationAndSortingSettings = (automationableArray, state) => {
  const sortedAutomationables = automationableArray.sort((a, b) => {
    if (a.model?.data?.pending_confirmation && !b.model?.data?.pending_confirmation) {
      return -1;
    }
    if (!a.model?.data?.pending_confirmation && b.model?.data?.pending_confirmation) {
      return 1;
    }
    return 0;
  });

  return sortedAutomationables.filter((automationable) => {
    const showEmail = (automationable.model.data.send_email
        || automationable.model.data.type === AutomationTypeEnum.CAMPAIGN)
        && state.showEmailAutomations;
    const showSignal = automationable.model.data.create_signal && state.showSignalAutomations;
    const showTask = automationable.model.data.create_task && state.showTaskAutomations;
    const showAllOff = !(automationable.model.data.send_email
      || automationable.model.data.type === AutomationTypeEnum.CAMPAIGN)
      && !automationable.model.data.create_signal
      && !automationable.model.data.create_task;
    const showQuery = !state.searchQuery || String(automationable.model.data.title)
      .toUpperCase()
      .includes(state.searchQuery.toUpperCase());
    return (showEmail || showSignal || showTask || showAllOff) && showQuery;
  });
};
const onceAutomationables = (state) => sortByPendingConfirmationAndSortingSettings(
  state.onceAutomationables,
  state,
);
const inactiveAutomationables = (state) => sortByPendingConfirmationAndSortingSettings(
  state.inactiveAutomationables,
  state,
);
const activeAutomationables = (state) => sortByPendingConfirmationAndSortingSettings(
  state.activeAutomationables,
  state,
);
const automationResultsPagination = (state) => state.automationResultsPagination;
const extendingSearches = (state) => state.extendingSearches;
const dynamicExtendingSearches = (state) => state.dynamicExtendingSearches;
const selectedDynamicExtendingSearchesIds = (state) => state.selectedDynamicExtendingSearchesIds;
const selectedAutomation = (state) => state.selectedAutomation;
const selectedGlobalAutomation = (state) => {
  if (
    state.selectedGlobalAutomationConfiguration !== null
    && state.selectedGlobalAutomationConfiguration !== undefined
    && state.selectedGlobalAutomationConfiguration.global_automation !== null
    && state.selectedGlobalAutomationConfiguration.global_automation !== undefined
    && state.selectedGlobalAutomationConfiguration.global_automation.data !== null
    && state.selectedGlobalAutomationConfiguration.global_automation.data !== undefined) {
    return state.selectedGlobalAutomationConfiguration.global_automation.data;
  }

  return null;
};
const loading = (state) => state.fetchingGlobalAutomations || state.fetchingAutomations;
// eslint-disable-next-line max-len
const selectedGlobalAutomationConfiguration = (state) => state.selectedGlobalAutomationConfiguration;
const selectedCreatingAutomationAction = (state) => state.automationActions
  .find((automationAction) => automationAction.id
    === state.creatingAutomation.automation_action_id);
const selectedCreatingSearch = (state) => state.searches
  .find((search) => search.hash_id.toString() === state.creatingAutomation.search_id?.toString());
const selectedExtendingSearches = (state) => state.extendingSearches
  .filter((extendingSearch) => state.selectedExtendingSearchesIds.includes(extendingSearch.id));
const selectedDynamicExtendingSearches = (state) => state.dynamicExtendingSearches
  .filter((dynamicExtendingSearch) => state.selectedDynamicExtendingSearchesIds
    .includes(dynamicExtendingSearch.id));

const activeStep = (state) => state.activeStep;

const users = (state) => state.users;

// Helper function to check if a value is neither null nor undefined
const isDefined = (value) => value != null && value !== "";

// Helper function to check if a value is neither null nor undefined
const isFilled = (value) => Array.isArray(value) && value.length > 0;

// Helper function to validate settings
const validateGenericSettings = (automation) => isDefined(automation.run_interval)
  && isDefined(automation.title)
  && isDefined(automation.description);

const validateEmailSettings = (automation) => isDefined(automation.email_user_id)
  && isDefined(automation.email_account_id)
  && isDefined(automation.email_template_id);

const validateSignalSettings = (automation) => {
  let isValid = isDefined(automation.signal_title)
    && isDefined(automation.signal_description)
    && (
      isFilled(automation.signal_assigned_users)
      || isFilled(automation.signal_assigned_user_departments)
    );

  if (automation.signal_visible_for_customer) {
    isValid = isValid && isDefined(automation.signal_customer_description);
  }

  if (automation.signal_auto_process) {
    isValid = isValid && isDefined(automation.signal_process_after);
  }

  return isValid;
};
const unconfiguredGlobalAutomations = (state) => state.unconfiguredGlobalAutomations;
const sendEmail = (state) => state.creatingAutomation?.send_email;
const createSignal = (state) => state.creatingAutomation?.create_signal;
const createTask = (state) => state.creatingAutomation?.create_task;
const validateTaskSettings = (automation) => isDefined(automation.task_type)
  && isDefined(automation.task_title)
  && isDefined(automation.task_description)
  && isDefined(automation.task_category)
  && isDefined(automation.task_priority)
  && isDefined(automation.task_create_in_days)
  && (
    isFilled(automation.task_assigned_users)
    || isFilled(automation.task_assigned_user_departments)
  );

const isNextStepEnabled = (state) => {
  // eslint-disable-next-line no-shadow
  const { activeStep, creatingAutomation } = state;
  // eslint-disable-next-line camelcase
  const { type, search_id, automation_action_id } = creatingAutomation;

  if (activeStep === AutomationCreateStepEnum.PROCESS) {
    return isDefined(type);
  }

  if (activeStep === AutomationCreateStepEnum.SETTINGS) {
    let isValid = validateGenericSettings(creatingAutomation);

    if (creatingAutomation.send_email) {
      isValid = isValid && validateEmailSettings(creatingAutomation);
    }

    if (creatingAutomation.create_task) {
      isValid = isValid && validateTaskSettings(creatingAutomation);
    }

    if (creatingAutomation.create_signal) {
      isValid = isValid && validateSignalSettings(creatingAutomation);
    }

    return isValid;
  }

  if (activeStep === AutomationCreateStepEnum.SELECTION) {
    if (type === AutomationTypeEnum.DATA_ENRICHMENT) {
      return isDefined(search_id) && isDefined(automation_action_id);
    }

    if (type === AutomationTypeEnum.CAMPAIGN || type === AutomationTypeEnum.SELECTION) {
      return isDefined(search_id);
    }
  }

  return true;
};

const creatingAutomation = (state) => state.creatingAutomation;

const taskTemplates = (state) => state.taskTemplates;

const automationResults = (state) => state.automationResults;
const hasAutomationResults = (state) => state.automationResults.length > 0;
const selectedAutomationResults = (state) => state.selectedAutomationResults;

const ignoredAutomationResults = (state) => state.ignoredAutomationResults;
const hasSelectedAutomationResults = (state) => state.selectedAutomationResults.length > 0;

const pendingConfirmationCount = (state) => state.pendingConfirmationCount;

// sorting
const selectedSortingCategory = (state) => state.selectedSortingCategory;
const showEmailAutomations = (state) => state.showEmailAutomations;
const showSignalAutomations = (state) => state.showSignalAutomations;
const showTaskAutomations = (state) => state.showTaskAutomations;
const searchQuery = (state) => state.searchQuery;

export default {
  me,
  onceAutomationables,
  activeAutomationables,
  inactiveAutomationables,
  ignoredAutomationResults,
  unconfiguredGlobalAutomations,
  users,
  automationActions,
  automations,
  globalAutomations,
  globalSearches,
  searches,
  searchesGroupedByType,
  extendingSearches,
  dynamicExtendingSearches,
  selectedDynamicExtendingSearchesIds,
  selectedAutomation,
  selectedGlobalAutomation,
  selectedGlobalAutomationConfiguration,
  selectedCreatingAutomationAction,
  selectedCreatingSearch,
  selectedExtendingSearches,
  selectedDynamicExtendingSearches,
  isNextStepEnabled,
  activeStep,
  creatingAutomation,
  taskTemplates,
  automationResults,
  hasAutomationResults,
  selectedAutomationResults,
  hasSelectedAutomationResults,
  sendEmail,
  createSignal,
  createTask,
  loading,
  automationResultsPagination,
  automationables,
  getField,
  pendingConfirmationCount,
  selectedSortingCategory,
  showEmailAutomations,
  showSignalAutomations,
  showTaskAutomations,
  searchQuery,
};
